/// IRPCS Part C navigation lights catalogue. Each vessel entry
/// captures the lights a mariner would see HEAD-ON at night — the
/// most common identification scenario and the angle the COLREGS
/// rules are most often quoted from. Stern and beam variants slot
/// in as future entries (`-stern`, `-beam` suffixes) when we add
/// orientation gameplay.
///
/// Coordinates use a 100×100 viewbox with (0,0) at the upper-left;
/// the renderer scales to any pixel size. The "horizon" sits at
/// y=80 — anything above is "in the air"; sea/silhouette draws
/// below. Lights are positioned relative to the silhouette that
/// the renderer outlines.

export type LightColor = "white" | "red" | "green" | "yellow";

export type LightCharacteristic = "fixed" | "flashing";

export interface Light {
  /// 0–100 viewbox X. 50 = centre of vessel.
  x: number;
  /// 0–100 viewbox Y. Lower numbers = higher in the sky.
  y: number;
  color: LightColor;
  /// Defaults to `fixed`. Flashing lights are animated by the
  /// renderer at ~120 flashes per minute (the IRPCS reference rate
  /// for "flashing" — Rule 21(f)).
  characteristic?: LightCharacteristic;
  /// Radius in viewbox units. Defaults to 3.
  radius?: number;
  /// Set true for all-round lights on the FAR side of the vessel
  /// from the current viewpoint. Per Rule 21 these are still
  /// visible (360° arc) but appear "behind the mast" — the
  /// renderer dims them and may offset their position slightly so
  /// the near-side lights read as visually closer. Used today only
  /// by the dredger's two-reds / two-greens side markers when
  /// looking at it from the opposite-side beam.
  farSide?: boolean;
}

/// Lights as seen from a specific aspect. Per IRPCS Part C:
///   - `headOn`: masthead(s) forward 225°, both sidelights
///     (red port, green starboard), no sternlight visible.
///   - `stern`: sternlight 135°, no sidelights, no masthead
///     (mastheads are forward-only).
///   - `portBeam`: viewer is to the **port (left) side** of the
///     vessel, looking at its port hull. Masthead visible (the
///     225° forward arc reaches just past the beam), red port
///     sidelight visible (it covers ahead → just abaft the port
///     beam), green stbd sidelight NOT visible, sternlight
///     visible (135° abaft-each-beam arc just covers the beam).
///   - `starboardBeam`: mirror of `portBeam`. Green stbd
///     sidelight visible, red NOT.
///
/// All-round lights (anchor, NUC, RAM, fishing, pilot,
/// hovercraft yellow, draft-restricted) are 360° — always visible.
///
/// Beam-view convention on the 100×100 viewbox: the vessel's bow
/// is rendered to the side closer to the visible sidelight (so
/// the "ship moving across in front of you" picture stays right
/// way round). For `portBeam` the bow points to viewer's RIGHT —
/// masthead near x=65, stern near x=20. `starboardBeam` mirrors.
export type Viewpoint = "headOn" | "stern" | "portBeam" | "starboardBeam";

export interface VesselSpec {
  /// Catalogue ID — stable across versions, used as the answer
  /// key in the flashcards game.
  id: string;
  /// Short label the player picks from a 6-way choice.
  name: string;
  /// One-line summary of what the vessel is doing.
  description: string;
  /// IRPCS rule citation, surfaced in the post-card explainer.
  rule: string;
  /// One light arrangement per viewpoint. Every vessel must
  /// supply `headOn` and `stern`; the picker switches between
  /// them per card so the player learns to read both aspects.
  views: Readonly<Record<Viewpoint, readonly Light[]>>;
  /// IRPCS Rule 35 sound signal for this vessel in restricted
  /// visibility. Plain-English transcription; surfaced in the
  /// flashcards reveal so the player learns light + fog signal
  /// together.
  fogSignal: string;
  /// Structured fog-signal pattern for audio playback. Sequence of
  /// elements played once per cycle; the player decides whether to
  /// loop. `P` = prolonged (4 s tone), `S` = short (1 s tone),
  /// `BELL` = rapid bell ringing (~5 s tremolo). The gap between
  /// elements is 1 s; the gap between cycle repeats is the
  /// difference between sum-of-elements and `cycleSeconds`.
  fogPattern: FogPattern;
  /// Daytime shape arrangement, stacked top-to-bottom on the main
  /// hoist. Empty array for vessels that have no prescribed day
  /// shape (e.g. an under-way power vessel just shows herself — no
  /// shape required).
  dayShapes: readonly DayShape[];
  /// Optional auxiliary shapes hoisted on a side bracket / yardarm
  /// to the SAFE-PASSAGE side. Per Rule 27(d)(ii), dredgers and
  /// vessels engaged in underwater operations show two diamonds in
  /// a vertical line to indicate the side another vessel may pass.
  /// Renderer draws these in a column right of the main hoist.
  dayShapesSafeSide?: readonly DayShape[];
  /// Optional auxiliary shapes hoisted on a side bracket / yardarm
  /// to the OBSTRUCTION side. Per Rule 27(d)(ii), two balls in a
  /// vertical line indicate the side on which the obstruction
  /// exists — DO NOT pass that side. Renderer draws these in a
  /// column left of the main hoist.
  dayShapesObstructionSide?: readonly DayShape[];
  /// Plain-English description of the day shape rule, mirroring
  /// the `fogSignal` / `rule` fields. Surfaced in the day-shapes
  /// flashcards reveal.
  dayShapeRule: string;
  /// For vessels where the light arrangement changes between
  /// making-way and not-making-way (NUC, RAM, fishing, sail-with-
  /// power, mine-clearance, towing): a short note describing what
  /// the player sees when the vessel is NOT making way through the
  /// water. The rendered `views` always depict the making-way case;
  /// the note explains what disappears when stopped or stationary.
  /// Undefined for vessels that look identical in both states
  /// (e.g. power-driven, anchored, hovercraft).
  makesWayNote?: string;
}

export type FogElement = "P" | "S" | "BELL";

export interface FogPattern {
  elements: readonly FogElement[];
  /// Spacing in seconds between repeats of the full pattern. The
  /// game only ever plays one cycle, but this is surfaced in the
  /// reveal so the player learns "every 1 min" vs "every 2 min".
  cycleSeconds: number;
}

/// IRPCS Part C daytime shapes (Rules 24–30). Each shape is a
/// solid black silhouette hoisted where it can best be seen — the
/// daytime equivalent of the nighttime light arrangement. Shapes
/// stack vertically when more than one is shown.
///
///   - `ball`                 — black sphere
///   - `diamond`              — single rhombus (lozenge) — middle
///                              shape of RAM (Rule 27(b)), towing
///                              > 200 m (Rule 24(a)(iv)), vessel
///                              being towed > 200 m (Rule 24(e)(ii)).
///   - `two-cones-apex`       — two cones with their apexes touching
///                              in the middle (hourglass). Fishing
///                              day shape (Rule 26(a)). Distinct
///                              from the single diamond.
///   - `three-balls-triangle` — three balls in an upward triangle
///                              (one foremast head, one at each
///                              yard end). Mine clearance
///                              (Rule 27(f)).
///   - `cone-up`              — solid triangle, apex up
///   - `cone-down`            — solid triangle, apex down (sailing
///                              under sail-and-power)
///   - `cylinder`             — vertical rectangle (constrained by
///                              draft)
export type DayShape =
  | "ball"
  | "diamond"
  | "two-cones-apex"
  | "three-balls-triangle"
  | "cone-up"
  | "cone-down"
  | "cylinder";

/// Headline catalogue. Twelve entries cover the bulk of the RYA
/// Yachtmaster / DSO / DOR exam syllabus; expand here as the game
/// matures. Order is roughly "frequency of encounter" so the
/// flashcards game shows common ships before edge-cases.
/// Per-vessel lights from each viewpoint. Common geometry:
///   - Mastheads are FORWARD-facing only (Rule 21(a) — 225° arc).
///     Invisible from astern.
///   - Sidelights are 112.5° forward of the beam on each side —
///     invisible from astern.
///   - Sternlight is 135° abaft each beam — invisible from ahead.
///   - All-round lights (anchor, NUC, RAM, draft, fishing, pilot,
///     hovercraft yellow) are 360° — visible from every angle.
///
/// On the 100×100 viewbox: stern-view sternlight sits low-centre
/// near the horizon; all-rounds keep their head-on Y but re-flow
/// into a single vertical stack centred on the silhouette.
export const NAV_LIGHTS: Readonly<Record<string, VesselSpec>> = Object.freeze({
  power_under_50m: {
    id: "power_under_50m",
    name: "Power-driven vessel < 50 m",
    description: "Under way and making way, less than 50 m in length.",
    rule: "Rule 23(a) — masthead light + sidelights + sternlight.",
    fogSignal: "1 prolonged blast every 2 min (making way).",
    fogPattern: { elements: ["P"], cycleSeconds: 120 },
    dayShapes: [],
    dayShapeRule: "Rule 23 — no day shape prescribed for a power-driven vessel under way.",
    views: {
      headOn: [
        { x: 50, y: 30, color: "white" }, // masthead, forward, white 225°
        { x: 35, y: 55, color: "red" }, // port sidelight (viewer's left)
        { x: 65, y: 55, color: "green" }, // stbd sidelight (viewer's right)
      ],
      stern: [
        { x: 50, y: 60, color: "white" }, // sternlight only
      ],
      portBeam: [
        { x: 65, y: 30, color: "white" }, // masthead, bow-side (viewer's right)
        { x: 50, y: 55, color: "red" }, // port sidelight, beam (centre)
      ],
      starboardBeam: [
        { x: 35, y: 30, color: "white" }, // masthead, bow-side (viewer's left)
        { x: 50, y: 55, color: "green" }, // stbd sidelight, beam (centre)
      ],
    },
  },
  power_over_50m: {
    id: "power_over_50m",
    name: "Power-driven vessel ≥ 50 m",
    description: "Two masthead lights in a vertical line — second one aft + higher.",
    rule: "Rule 23(a) — additional masthead aft of and above the forward one.",
    fogSignal: "1 prolonged blast every 2 min (making way).",
    fogPattern: { elements: ["P"], cycleSeconds: 120 },
    dayShapes: [],
    dayShapeRule: "Rule 23 — no day shape prescribed for a power-driven vessel under way.",
    views: {
      headOn: [
        { x: 50, y: 20, color: "white" }, // aft masthead (higher)
        { x: 50, y: 40, color: "white" }, // forward masthead
        { x: 35, y: 60, color: "red" },
        { x: 65, y: 60, color: "green" },
      ],
      stern: [
        { x: 50, y: 60, color: "white" },
      ],
      portBeam: [
        { x: 70, y: 18, color: "white" }, // aft masthead (higher) — viewer's right-of-centre
        { x: 55, y: 35, color: "white" }, // forward masthead
        { x: 50, y: 55, color: "red" }, // port sidelight, beam
      ],
      starboardBeam: [
        { x: 30, y: 18, color: "white" },
        { x: 45, y: 35, color: "white" },
        { x: 50, y: 55, color: "green" },
      ],
    },
  },
  sailing: {
    id: "sailing",
    name: "Sailing vessel under way",
    description: "Under sail (no masthead light).",
    rule: "Rule 25(a) — sidelights + sternlight; no masthead.",
    fogSignal: "1 prolonged + 2 short blasts every 2 min.",
    fogPattern: { elements: ["P", "S", "S"], cycleSeconds: 120 },
    dayShapes: [],
    dayShapeRule: "Rule 25 — no day shape when under sail alone. If also under propulsion, show a cone apex-down forward (see sail-with-power).",
    views: {
      headOn: [
        { x: 35, y: 55, color: "red" },
        { x: 65, y: 55, color: "green" },
      ],
      stern: [
        { x: 50, y: 60, color: "white" },
      ],
      portBeam: [
        { x: 50, y: 55, color: "red" }, // port sidelight (no masthead — under sail)
      ],
      starboardBeam: [
        { x: 50, y: 55, color: "green" },
      ],
    },
  },
  anchored_under_50m: {
    id: "anchored_under_50m",
    name: "Vessel at anchor < 50 m",
    description: "Not moving; a single all-round white light forward.",
    rule: "Rule 30(a)(i) — one all-round white light where best seen.",
    fogSignal: "Rapid bell ringing for ~5 sec every 1 min.",
    fogPattern: { elements: ["BELL"], cycleSeconds: 60 },
    dayShapes: ["ball"],
    dayShapeRule: "Rule 30(a)(i) — one black ball forward where best seen.",
    // All-round → identical from any aspect.
    views: {
      headOn: [{ x: 50, y: 35, color: "white" }],
      stern: [{ x: 50, y: 35, color: "white" }],
      portBeam: [{ x: 50, y: 35, color: "white" }],
      starboardBeam: [{ x: 50, y: 35, color: "white" }],
    },
  },
  anchored_over_50m: {
    id: "anchored_over_50m",
    name: "Vessel at anchor ≥ 50 m",
    description: "Two all-round white lights — one forward, one aft, forward higher.",
    rule: "Rule 30(a)(ii) — one forward, one aft; lower aft.",
    fogSignal: "Bell forward + gong aft, every 1 min (≥ 100 m only).",
    fogPattern: { elements: ["BELL"], cycleSeconds: 60 },
    dayShapes: ["ball"],
    dayShapeRule: "Rule 30(a)(i) — one black ball forward (the ball is identical regardless of vessel size).",
    views: {
      headOn: [
        { x: 50, y: 30, color: "white" }, // forward (higher)
        { x: 50, y: 55, color: "white" }, // aft (lower)
      ],
      stern: [
        { x: 50, y: 30, color: "white" },
        { x: 50, y: 55, color: "white" },
      ],
      // Beam views the spatial separation is visible — forward
      // light is toward the bow side, aft light toward the stern.
      portBeam: [
        { x: 65, y: 28, color: "white" }, // forward (bow-side, higher)
        { x: 30, y: 50, color: "white" }, // aft (stern-side, lower)
      ],
      starboardBeam: [
        { x: 35, y: 28, color: "white" },
        { x: 70, y: 50, color: "white" },
      ],
    },
  },
  fishing_trawling: {
    id: "fishing_trawling",
    name: "Trawler under way",
    description: "Green-over-white all-round, plus sidelights when making way.",
    rule: "Rule 26(b) — green over white all-round; no forward masthead.",
    fogSignal: "1 prolonged + 2 short blasts every 2 min.",
    fogPattern: { elements: ["P", "S", "S"], cycleSeconds: 120 },
    dayShapes: ["two-cones-apex"],
    dayShapeRule: "Rule 26(a) — two black cones with their apexes together in a vertical line (an hourglass silhouette). Identical for trawlers and other fishing vessels.",
    makesWayNote: "When NOT making way (gear set, drifting on the catch), the sidelights and sternlight are extinguished — only the green-over-white all-rounds remain.",
    views: {
      headOn: [
        { x: 50, y: 25, color: "green" },
        { x: 50, y: 40, color: "white" },
        { x: 35, y: 60, color: "red" },
        { x: 65, y: 60, color: "green" },
      ],
      stern: [
        { x: 50, y: 25, color: "green" },
        { x: 50, y: 40, color: "white" },
        { x: 50, y: 60, color: "white" }, // sternlight
      ],
      portBeam: [
        { x: 50, y: 25, color: "green" }, // all-round
        { x: 50, y: 40, color: "white" }, // all-round
        { x: 50, y: 55, color: "red" }, // port sidelight
      ],
      starboardBeam: [
        { x: 50, y: 25, color: "green" },
        { x: 50, y: 40, color: "white" },
        { x: 50, y: 55, color: "green" },
      ],
    },
  },
  fishing_other: {
    id: "fishing_other",
    name: "Fishing (other than trawling)",
    description: "Red-over-white all-round — e.g. long-lining, drift-netting.",
    rule: "Rule 26(c) — red over white all-round.",
    fogSignal: "1 prolonged + 2 short blasts every 2 min.",
    fogPattern: { elements: ["P", "S", "S"], cycleSeconds: 120 },
    dayShapes: ["two-cones-apex"],
    dayShapeRule: "Rule 26(a) — two black cones with their apexes together in a vertical line (an hourglass silhouette). Identical for trawlers and other fishing vessels. If outlying gear extends > 150 m, add a cone apex-up pointing toward the gear (Rule 26(c)(ii)).",
    makesWayNote: "When NOT making way (hauling, drifting on a long-line), the sidelights and sternlight are extinguished — only the red-over-white all-rounds remain.",
    views: {
      headOn: [
        { x: 50, y: 25, color: "red" },
        { x: 50, y: 40, color: "white" },
        { x: 35, y: 60, color: "red" },
        { x: 65, y: 60, color: "green" },
      ],
      stern: [
        { x: 50, y: 25, color: "red" },
        { x: 50, y: 40, color: "white" },
        { x: 50, y: 60, color: "white" },
      ],
      portBeam: [
        { x: 50, y: 25, color: "red" },
        { x: 50, y: 40, color: "white" },
        { x: 50, y: 55, color: "red" },
      ],
      starboardBeam: [
        { x: 50, y: 25, color: "red" },
        { x: 50, y: 40, color: "white" },
        { x: 50, y: 55, color: "green" },
      ],
    },
  },
  not_under_command: {
    id: "not_under_command",
    name: "Not under command",
    description: "Disabled — unable to manoeuvre. Two red all-round in a vertical line.",
    rule: "Rule 27(a) — two red all-round in a vertical line; sidelights only when making way.",
    fogSignal: "1 prolonged + 2 short blasts every 2 min.",
    fogPattern: { elements: ["P", "S", "S"], cycleSeconds: 120 },
    dayShapes: ["ball", "ball"],
    dayShapeRule: "Rule 27(a)(ii) — two balls in a vertical line.",
    makesWayNote: "When NOT making way (drifting on a dead engine), the sidelights and sternlight are extinguished — only the two red all-rounds (or the two balls by day) remain.",
    // All-round only (assuming NUC not making way) → identical
    // from every aspect.
    views: {
      headOn: [
        { x: 50, y: 25, color: "red" },
        { x: 50, y: 40, color: "red" },
      ],
      stern: [
        { x: 50, y: 25, color: "red" },
        { x: 50, y: 40, color: "red" },
      ],
      portBeam: [
        { x: 50, y: 25, color: "red" },
        { x: 50, y: 40, color: "red" },
      ],
      starboardBeam: [
        { x: 50, y: 25, color: "red" },
        { x: 50, y: 40, color: "red" },
      ],
    },
  },
  restricted_ability: {
    id: "restricted_ability",
    name: "Restricted in ability to manoeuvre",
    description: "Red-white-red all-round in a vertical line — e.g. cable-laying, dredging.",
    rule: "Rule 27(b) — red-white-red in a vertical line plus normal lights.",
    fogSignal: "1 prolonged + 2 short blasts every 2 min.",
    fogPattern: { elements: ["P", "S", "S"], cycleSeconds: 120 },
    dayShapes: ["ball", "diamond", "ball"],
    dayShapeRule: "Rule 27(b)(ii) — ball, diamond, ball in a vertical line.",
    makesWayNote: "When NOT making way, the sidelights, sternlight, and masthead light are extinguished — only the red-white-red all-rounds (or the ball-diamond-ball by day) remain.",
    views: {
      headOn: [
        { x: 50, y: 20, color: "red" },
        { x: 50, y: 32, color: "white" },
        { x: 50, y: 44, color: "red" },
        { x: 35, y: 65, color: "red" },
        { x: 65, y: 65, color: "green" },
      ],
      stern: [
        { x: 50, y: 20, color: "red" },
        { x: 50, y: 32, color: "white" },
        { x: 50, y: 44, color: "red" },
        { x: 50, y: 70, color: "white" },
      ],
      portBeam: [
        { x: 50, y: 20, color: "red" },
        { x: 50, y: 32, color: "white" },
        { x: 50, y: 44, color: "red" },
        { x: 50, y: 58, color: "red" }, // port sidelight (when making way)
      ],
      starboardBeam: [
        { x: 50, y: 20, color: "red" },
        { x: 50, y: 32, color: "white" },
        { x: 50, y: 44, color: "red" },
        { x: 50, y: 58, color: "green" },
      ],
    },
  },
  dredging_underwater: {
    id: "dredging_underwater",
    name: "Dredging / underwater operations",
    description:
      "RAM lights plus side markers indicating which side to pass — two red over each other on the obstruction side, two green over each other on the clear side.",
    rule:
      "Rule 27(d) — RAM signal (red-white-red) + two red lights vertical on the obstruction side + two green lights vertical on the safe-passage side.",
    fogSignal: "1 prolonged + 2 short blasts every 2 min (as for RAM).",
    fogPattern: { elements: ["P", "S", "S"], cycleSeconds: 120 },
    dayShapes: ["ball", "diamond", "ball"],
    dayShapeRule:
      "Rule 27(d)(ii) — ball-diamond-ball on the main hoist, plus two balls vertical on the OBSTRUCTION side (do not pass) and two diamonds vertical on the SAFE-PASSAGE side (pass this side).",
    dayShapesObstructionSide: ["ball", "ball"],
    dayShapesSafeSide: ["diamond", "diamond"],
    makesWayNote:
      "Only seen when restricted in her ability to manoeuvre due to the nature of the work. When the vessel is also engaged in diving operations and too small to hoist the full signal, a rigid replica of flag A is exhibited instead.",
    views: {
      headOn: [
        // Main hoist — RAM
        { x: 50, y: 18, color: "red" },
        { x: 50, y: 30, color: "white" },
        { x: 50, y: 42, color: "red" },
        // Side markers — two reds left (obstruction), two greens right (safe)
        { x: 28, y: 32, color: "red" },
        { x: 28, y: 44, color: "red" },
        { x: 72, y: 32, color: "green" },
        { x: 72, y: 44, color: "green" },
        // Sidelights (when making way)
        { x: 35, y: 70, color: "red" },
        { x: 65, y: 70, color: "green" },
      ],
      stern: [
        { x: 50, y: 18, color: "red" },
        { x: 50, y: 30, color: "white" },
        { x: 50, y: 42, color: "red" },
        { x: 28, y: 32, color: "red" },
        { x: 28, y: 44, color: "red" },
        { x: 72, y: 32, color: "green" },
        { x: 72, y: 44, color: "green" },
        { x: 50, y: 70, color: "white" },
      ],
      portBeam: [
        { x: 50, y: 18, color: "red" },
        { x: 50, y: 30, color: "white" },
        { x: 50, y: 42, color: "red" },
        // Near side (vessel's port = obstruction): two reds.
        { x: 30, y: 32, color: "red" },
        { x: 30, y: 44, color: "red" },
        // Far side (vessel's starboard = safe-passage): two greens
        // are all-round so technically visible; drawn dimmed and
        // slightly offset past the mast to suggest perspective.
        { x: 56, y: 32, color: "green", farSide: true },
        { x: 56, y: 44, color: "green", farSide: true },
        { x: 50, y: 60, color: "red" },
      ],
      starboardBeam: [
        { x: 50, y: 18, color: "red" },
        { x: 50, y: 30, color: "white" },
        { x: 50, y: 42, color: "red" },
        // Near side (vessel's starboard = safe-passage): two greens.
        { x: 70, y: 32, color: "green" },
        { x: 70, y: 44, color: "green" },
        // Far side (vessel's port = obstruction): two reds drawn
        // dimmed behind the mast.
        { x: 44, y: 32, color: "red", farSide: true },
        { x: 44, y: 44, color: "red", farSide: true },
        { x: 50, y: 60, color: "green" },
      ],
    },
  },
  constrained_by_draft: {
    id: "constrained_by_draft",
    name: "Constrained by her draft",
    description: "Three red all-round in a vertical line — deep-draft commercial.",
    rule: "Rule 28 — three red all-round in a vertical line + normal lights.",
    fogSignal: "1 prolonged blast every 2 min (power-driven making way).",
    fogPattern: { elements: ["P"], cycleSeconds: 120 },
    dayShapes: ["cylinder"],
    dayShapeRule: "Rule 28 — a cylinder (vertical black cylinder shape).",
    views: {
      headOn: [
        { x: 50, y: 15, color: "red" },
        { x: 50, y: 27, color: "red" },
        { x: 50, y: 39, color: "red" },
        { x: 50, y: 50, color: "white" }, // forward masthead
        { x: 35, y: 65, color: "red" },
        { x: 65, y: 65, color: "green" },
      ],
      stern: [
        // Three all-round reds + sternlight; lose masthead + sidelights.
        { x: 50, y: 15, color: "red" },
        { x: 50, y: 27, color: "red" },
        { x: 50, y: 39, color: "red" },
        { x: 50, y: 65, color: "white" }, // sternlight
      ],
      portBeam: [
        { x: 50, y: 15, color: "red" }, // all-round stack stays centred
        { x: 50, y: 27, color: "red" },
        { x: 50, y: 39, color: "red" },
        { x: 65, y: 48, color: "white" }, // masthead, bow-side
        { x: 50, y: 60, color: "red" }, // port sidelight
      ],
      starboardBeam: [
        { x: 50, y: 15, color: "red" },
        { x: 50, y: 27, color: "red" },
        { x: 50, y: 39, color: "red" },
        { x: 35, y: 48, color: "white" },
        { x: 50, y: 60, color: "green" },
      ],
    },
  },
  pilot_on_duty: {
    id: "pilot_on_duty",
    name: "Pilot vessel on duty",
    description: "White-over-red all-round at the masthead.",
    rule: "Rule 29(a)(i) — white over red all-round at the masthead.",
    fogSignal: "4 short blasts every 2 min (in addition to power-driven).",
    fogPattern: { elements: ["S", "S", "S", "S"], cycleSeconds: 120 },
    dayShapes: [],
    dayShapeRule: "Rule 29 — no day shape; a pilot vessel is identified by the white-and-red H flag during the day.",
    views: {
      headOn: [
        { x: 50, y: 25, color: "white" },
        { x: 50, y: 40, color: "red" },
        { x: 35, y: 60, color: "red" },
        { x: 65, y: 60, color: "green" },
      ],
      stern: [
        { x: 50, y: 25, color: "white" },
        { x: 50, y: 40, color: "red" },
        { x: 50, y: 65, color: "white" }, // sternlight
      ],
      portBeam: [
        { x: 50, y: 25, color: "white" }, // all-round stack
        { x: 50, y: 40, color: "red" },
        { x: 50, y: 58, color: "red" }, // port sidelight
      ],
      starboardBeam: [
        { x: 50, y: 25, color: "white" },
        { x: 50, y: 40, color: "red" },
        { x: 50, y: 58, color: "green" },
      ],
    },
  },
  hovercraft: {
    id: "hovercraft",
    name: "Air-cushion vessel (non-displacement)",
    description: "Power-driven lights plus a flashing yellow all-round.",
    rule: "Rule 23(b) — power-driven lights + flashing yellow all-round.",
    fogSignal: "1 prolonged blast every 2 min (power-driven making way).",
    fogPattern: { elements: ["P"], cycleSeconds: 120 },
    dayShapes: [],
    dayShapeRule: "Rule 23(b) — no day shape; in flight (non-displacement) mode the all-round yellow flashing light is the identifier.",
    views: {
      headOn: [
        { x: 50, y: 25, color: "yellow", characteristic: "flashing" },
        { x: 50, y: 40, color: "white" }, // masthead
        { x: 35, y: 60, color: "red" },
        { x: 65, y: 60, color: "green" },
      ],
      stern: [
        { x: 50, y: 25, color: "yellow", characteristic: "flashing" },
        { x: 50, y: 60, color: "white" }, // sternlight
      ],
      portBeam: [
        { x: 50, y: 25, color: "yellow", characteristic: "flashing" },
        { x: 65, y: 40, color: "white" }, // masthead, bow-side
        { x: 50, y: 55, color: "red" }, // port sidelight
      ],
      starboardBeam: [
        { x: 50, y: 25, color: "yellow", characteristic: "flashing" },
        { x: 35, y: 40, color: "white" },
        { x: 50, y: 55, color: "green" },
      ],
    },
  },
  aground: {
    id: "aground",
    name: "Vessel aground",
    description: "Stuck on the bottom — extends the anchor signal with two more red lights at night.",
    rule: "Rule 30(d) — anchor light + two all-round red lights vertical, with three balls vertical by day.",
    fogSignal: "Anchor signal preceded and followed by 3 separate distinct strokes of the bell.",
    fogPattern: { elements: ["BELL"], cycleSeconds: 60 },
    dayShapes: ["ball", "ball", "ball"],
    dayShapeRule: "Rule 30(d)(i) — three balls in a vertical line.",
    views: {
      headOn: [
        { x: 50, y: 18, color: "red" },
        { x: 50, y: 30, color: "red" },
        { x: 50, y: 45, color: "white" }, // anchor light
      ],
      stern: [
        { x: 50, y: 18, color: "red" },
        { x: 50, y: 30, color: "red" },
        { x: 50, y: 45, color: "white" },
      ],
      portBeam: [
        { x: 50, y: 18, color: "red" },
        { x: 50, y: 30, color: "red" },
        { x: 50, y: 45, color: "white" },
      ],
      starboardBeam: [
        { x: 50, y: 18, color: "red" },
        { x: 50, y: 30, color: "red" },
        { x: 50, y: 45, color: "white" },
      ],
    },
  },
  sail_with_power: {
    id: "sail_with_power",
    name: "Sailing vessel under sail + power",
    description: "Engine and sails both engaged — must show a cone apex-down forward by day.",
    rule: "Rule 25(e) — vessel proceeding under sail when also being propelled by machinery exhibits a conical shape, apex downwards, forward where it can best be seen.",
    fogSignal: "1 prolonged blast every 2 min (treated as a power-driven vessel).",
    fogPattern: { elements: ["P"], cycleSeconds: 120 },
    dayShapes: ["cone-down"],
    dayShapeRule: "Rule 25(e) — one conical shape, apex pointing downwards.",
    makesWayNote: "When NOT making way (motoring slowly to hold station, engine in neutral), the sidelights stay lit but the sternlight is extinguished. The cone remains hoisted.",
    views: {
      headOn: [
        { x: 50, y: 30, color: "white" }, // power-driven masthead
        { x: 35, y: 55, color: "red" },
        { x: 65, y: 55, color: "green" },
      ],
      stern: [{ x: 50, y: 60, color: "white" }],
      portBeam: [
        { x: 65, y: 30, color: "white" },
        { x: 50, y: 55, color: "red" },
      ],
      starboardBeam: [
        { x: 35, y: 30, color: "white" },
        { x: 50, y: 55, color: "green" },
      ],
    },
  },
  mine_clearance: {
    id: "mine_clearance",
    name: "Mine-clearance vessel",
    description:
      "Engaged in mine-clearance operations — three all-round green lights or three balls arranged in a triangle (one at foremast head, one at each end of the fore yard). Other vessels must keep at least 1000 m clear.",
    rule: "Rule 27(f) — three all-round green lights or three balls in a triangular arrangement, in addition to the lights for a power-driven vessel (Rule 23) or at anchor (Rule 30).",
    fogSignal: "1 prolonged + 2 short blasts every 2 min.",
    fogPattern: { elements: ["P", "S", "S"], cycleSeconds: 120 },
    dayShapes: ["three-balls-triangle"],
    dayShapeRule:
      "Rule 27(f) — three balls in a triangular arrangement: one at the foremast head and one at each end of the fore yard.",
    makesWayNote: "When NOT making way (sweeping a stationary search pattern), the sidelights and sternlight are extinguished — only the three all-round greens (or the three-balls triangle by day) remain.",
    views: {
      headOn: [
        // Three all-round greens in a triangle — top + two lower yardarms.
        { x: 50, y: 18, color: "green" },
        { x: 36, y: 34, color: "green" },
        { x: 64, y: 34, color: "green" },
        // Power-driven lights when making way.
        { x: 50, y: 50, color: "white" }, // masthead
        { x: 35, y: 68, color: "red" },
        { x: 65, y: 68, color: "green" },
      ],
      stern: [
        { x: 50, y: 18, color: "green" },
        { x: 36, y: 34, color: "green" },
        { x: 64, y: 34, color: "green" },
        { x: 50, y: 68, color: "white" }, // sternlight
      ],
      portBeam: [
        { x: 50, y: 18, color: "green" },
        { x: 36, y: 34, color: "green" },
        { x: 64, y: 34, color: "green" },
        { x: 65, y: 52, color: "white" }, // masthead, bow-side
        { x: 50, y: 64, color: "red" },
      ],
      starboardBeam: [
        { x: 50, y: 18, color: "green" },
        { x: 36, y: 34, color: "green" },
        { x: 64, y: 34, color: "green" },
        { x: 35, y: 52, color: "white" },
        { x: 50, y: 64, color: "green" },
      ],
    },
  },
  towing_long: {
    id: "towing_long",
    name: "Towing vessel — tow > 200 m",
    description:
      "Power-driven vessel towing astern when the length of tow exceeds 200 m. Three masthead lights vertical instead of the usual two, plus a yellow towing light above the sternlight.",
    rule: "Rule 24(a) — three masthead lights in a vertical line, sidelights, sternlight, towing light (yellow above the sternlight); diamond shape by day.",
    fogSignal: "1 prolonged + 2 short blasts every 2 min (Rule 35(c) — towing).",
    fogPattern: { elements: ["P", "S", "S"], cycleSeconds: 120 },
    dayShapes: ["diamond"],
    dayShapeRule:
      "Rule 24(a)(iv) — when the length of tow exceeds 200 m, a diamond shape where it can best be seen.",
    makesWayNote: "When NOT making way (slack tow, holding station against the tide), the towing light and sternlight stay lit — the rules treat a tug actively towing as under way regardless of forward motion.",
    views: {
      headOn: [
        { x: 50, y: 12, color: "white" }, // top masthead
        { x: 50, y: 25, color: "white" }, // middle masthead
        { x: 50, y: 38, color: "white" }, // forward (lowest) masthead
        { x: 35, y: 62, color: "red" },
        { x: 65, y: 62, color: "green" },
      ],
      stern: [
        { x: 50, y: 50, color: "yellow" }, // towing light
        { x: 50, y: 65, color: "white" }, // sternlight
      ],
      portBeam: [
        { x: 70, y: 14, color: "white" }, // top masthead, bow-side
        { x: 60, y: 28, color: "white" },
        { x: 50, y: 42, color: "white" }, // forward masthead
        { x: 50, y: 58, color: "red" }, // port sidelight
      ],
      starboardBeam: [
        { x: 30, y: 14, color: "white" },
        { x: 40, y: 28, color: "white" },
        { x: 50, y: 42, color: "white" },
        { x: 50, y: 58, color: "green" },
      ],
    },
  },
  vessel_being_towed: {
    id: "vessel_being_towed",
    name: "Vessel / barge being towed",
    description:
      "The vessel or barge on the OTHER end of the tow line. Sidelights + sternlight only — no masthead, no towing light. When the length of tow exceeds 200 m a diamond shape is hoisted by day.",
    rule: "Rule 24(e) — vessel or object being towed shall exhibit sidelights, a sternlight and, when the length of tow exceeds 200 m, a diamond shape where it can best be seen.",
    fogSignal:
      "1 prolonged + 3 short blasts every 2 min, sounded immediately after the tug's signal (Rule 35(e) — manned tow).",
    fogPattern: { elements: ["P", "S", "S", "S"], cycleSeconds: 120 },
    dayShapes: ["diamond"],
    dayShapeRule:
      "Rule 24(e)(ii) — when the length of tow exceeds 200 m, one diamond shape where it can best be seen.",
    makesWayNote:
      "An unmanned or partly-submerged tow follows Rule 24(g) instead and shows a different light pattern; this entry covers the typical manned barge under a long tow.",
    views: {
      headOn: [
        // Sidelights only — barge is low so the lights sit near the
        // deck. No masthead, no all-rounds.
        { x: 36, y: 62, color: "red" },
        { x: 64, y: 62, color: "green" },
      ],
      stern: [
        { x: 50, y: 62, color: "white" }, // sternlight
      ],
      portBeam: [
        { x: 50, y: 60, color: "red" },
      ],
      starboardBeam: [
        { x: 50, y: 60, color: "green" },
      ],
    },
  },
  pushing_ahead: {
    id: "pushing_ahead",
    name: "Tug pushing ahead / towing alongside",
    description:
      "A power-driven tug pushing a barge ahead (or towing alongside) that is NOT a rigid composite unit. Two masthead lights vertical instead of the usual one, plus the normal sidelights and sternlight. No yellow towing light (that's reserved for towing astern).",
    rule: "Rule 24(c) — two masthead lights in a vertical line, sidelights, sternlight. No diamond shape required.",
    fogSignal: "1 prolonged blast every 2 min (treated as a power-driven vessel under way).",
    fogPattern: { elements: ["P"], cycleSeconds: 120 },
    dayShapes: [],
    dayShapeRule:
      "Rule 24(c) — no day shape required. (A rigidly connected composite pushing unit per Rule 24(b) is treated as a single power-driven vessel.)",
    makesWayNote:
      "The vessel being pushed ahead exhibits sidelights at its forward end per Rule 24(f); a rigid composite unit shows the single set of Rule 23 power-driven lights for the combined hull.",
    views: {
      headOn: [
        { x: 50, y: 20, color: "white" }, // upper masthead
        { x: 50, y: 36, color: "white" }, // lower masthead
        { x: 35, y: 60, color: "red" },
        { x: 65, y: 60, color: "green" },
      ],
      stern: [
        { x: 50, y: 65, color: "white" },
      ],
      portBeam: [
        { x: 65, y: 22, color: "white" }, // upper masthead, bow-side
        { x: 55, y: 38, color: "white" }, // lower masthead
        { x: 50, y: 58, color: "red" },
      ],
      starboardBeam: [
        { x: 35, y: 22, color: "white" },
        { x: 45, y: 38, color: "white" },
        { x: 50, y: 58, color: "green" },
      ],
    },
  },
});

export const VESSEL_IDS: readonly string[] = Object.keys(NAV_LIGHTS);
